/**
 * SecuritySiteDetail Page (C.4)
 *
 * Site-level security drill-down: connectivity chain, traffic chart,
 * site-specific alerts, and admin-only session/login tables.
 */

import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import {
  Shield, ArrowLeft, Loader2, AlertTriangle,
  Radio, Server, Cloud, Network, Users, KeyRound,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import { useSecurityStore } from '../store/securityStore';
import { useAuthStore } from '../store/authStore';
import { useSiteDashboard } from '../hooks/useSecurity';
import { TrafficChart } from '../components/security/TrafficChart';
import { SessionTable } from '../components/security/SessionTable';
import { LoginActivityTable } from '../components/security/LoginActivityTable';

const ADMIN_ROLES = ['platform_admin', 'parent_org_admin', 'customer_admin'];

const STATUS_COLORS: Record<string, string> = {
  up: 'bg-ems-green',
  established: 'bg-ems-green',
  connected: 'bg-ems-green',
  degraded: 'bg-ems-amber',
  down: 'bg-ems-red',
  offline: 'bg-ems-red',
  unknown: 'bg-ems-text-muted',
};

function getStatusColor(status: string): string {
  return STATUS_COLORS[status] || STATUS_COLORS.unknown;
}

function getStatusBadge(status: string) {
  const label = status === 'up' ? 'Online' : status === 'degraded' ? 'Degraded' : status === 'down' ? 'Offline' : 'Unknown';
  const color = status === 'up' ? 'bg-ems-green/10 text-ems-green' : status === 'degraded' ? 'bg-ems-amber/10 text-ems-amber' : 'bg-ems-red/10 text-ems-red';
  return <span className={`px-2.5 py-1 rounded text-xs font-medium ${color}`}>{label}</span>;
}

function ConnectorLine({ status }: { status: string }) {
  const color = status === 'up' || status === 'established'
    ? 'bg-ems-green/40'
    : status === 'degraded'
    ? 'bg-ems-amber/40'
    : 'bg-ems-red/40';
  return <div className={`flex-1 h-0.5 ${color} mx-2`} />;
}

function ConnectivityNode({ icon: Icon, label, status }: {
  icon: React.ElementType;
  label: string;
  status: string;
}) {
  return (
    <div className="flex flex-col items-center gap-1 min-w-[60px]">
      <Icon className="w-5 h-5 text-ems-text-muted" />
      <div className={`w-3 h-3 rounded-full ${getStatusColor(status)} ring-2 ring-ems-bg-card`} />
      <span className="text-[10px] text-ems-text-muted font-medium">{label}</span>
    </div>
  );
}

function CollapsibleSection({ title, icon: Icon, children, defaultOpen = false }: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-ems-bg-card rounded-2xl border border-ems-border">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-ems-green" />
          <h2 className="text-sm font-semibold text-ems-text-primary">{title}</h2>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-ems-text-muted" /> : <ChevronDown className="w-4 h-4 text-ems-text-muted" />}
      </button>
      {open && (
        <div className="px-5 pb-5 pt-0">
          {children}
        </div>
      )}
    </div>
  );
}

export function SecuritySiteDetail() {
  const { siteId } = useParams<{ siteId: string }>();
  const { setSelectedSiteId } = useSecurityStore();
  const userRole = useAuthStore((state) => state.user?.role);
  const isAdmin = !!userRole && ADMIN_ROLES.includes(userRole);

  // Set selected site for TrafficChart
  const prevSiteId = useSecurityStore.getState().selectedSiteId;
  if (siteId && prevSiteId !== siteId) {
    setSelectedSiteId(siteId);
  }

  const { data: siteDashboard, isLoading } = useSiteDashboard(siteId || '');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-ems-text-muted" />
      </div>
    );
  }

  if (!siteDashboard) {
    return (
      <div className="space-y-4">
        <Link to="/security" className="flex items-center gap-1 text-sm text-ems-green hover:text-ems-green/80">
          <ArrowLeft className="w-4 h-4" /> Back to Security Dashboard
        </Link>
        <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-8 text-center">
          <Shield className="w-8 h-8 text-ems-text-muted/30 mx-auto mb-2" />
          <p className="text-sm text-ems-text-muted">Site not found</p>
        </div>
      </div>
    );
  }

  const { site, connectivity, alerts } = siteDashboard;
  const tunnelStatus = connectivity?.tunnel_status || 'unknown';
  const bgpStatus = connectivity?.bgp_status || 'unknown';

  return (
    <div className="space-y-6">
      {/* Back navigation */}
      <Link to="/security" className="flex items-center gap-1 text-sm text-ems-green hover:text-ems-green/80">
        <ArrowLeft className="w-4 h-4" /> Back to Security Dashboard
      </Link>

      {/* Site Header */}
      <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-ems-green" />
            <div>
              <h1 className="text-lg font-bold text-ems-text-primary">{site.name}</h1>
              <p className="text-xs text-ems-text-muted font-mono">{site.site_code}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {getStatusBadge(tunnelStatus)}
            {site.last_heartbeat && (
              <span className="text-xs text-ems-text-muted">
                Last heartbeat: {new Date(site.last_heartbeat).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Connectivity Chain — 5-node detailed view */}
      <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-5">
        <div className="flex items-center gap-2 mb-4">
          <Network className="w-4 h-4 text-ems-text-muted" />
          <h2 className="text-sm font-semibold text-ems-text-primary uppercase tracking-wider">
            Connectivity Chain
          </h2>
        </div>

        <div className="flex items-center justify-between px-4 py-6 bg-ems-bg rounded-lg">
          <ConnectivityNode
            icon={Radio}
            label="Site"
            status={tunnelStatus === 'up' ? 'up' : tunnelStatus}
          />
          <ConnectorLine status={tunnelStatus === 'up' ? 'up' : tunnelStatus} />
          <ConnectivityNode
            icon={Server}
            label="Router"
            status={tunnelStatus === 'up' ? 'up' : tunnelStatus}
          />
          <ConnectorLine status={tunnelStatus === 'up' ? 'up' : tunnelStatus} />
          <ConnectivityNode
            icon={Server}
            label="EMS"
            status={tunnelStatus === 'up' ? 'up' : tunnelStatus}
          />
          <ConnectorLine status={tunnelStatus} />
          <ConnectivityNode
            icon={Shield}
            label="VPN"
            status={tunnelStatus}
          />
          <ConnectorLine status={bgpStatus === 'established' ? 'up' : 'down'} />
          <ConnectivityNode
            icon={Cloud}
            label="Cloud"
            status={bgpStatus === 'established' ? 'up' : 'down'}
          />
        </div>

        <div className="flex items-center justify-between mt-3 px-2 text-xs text-ems-text-muted">
          <span>Tunnel: <strong className="text-ems-text-primary">{tunnelStatus}</strong></span>
          <span>BGP: <strong className="text-ems-text-primary">{bgpStatus}</strong></span>
          <span>Packet Loss: <strong className="text-ems-text-primary">{(connectivity?.packet_loss_pct ?? 0).toFixed(2)}%</strong></span>
        </div>
      </div>

      {/* Traffic Chart (parameterized for this site via securityStore.selectedSiteId) */}
      <TrafficChart />

      {/* Site-Specific Security Alerts */}
      <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-5">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-4 h-4 text-ems-text-muted" />
          <h2 className="text-sm font-semibold text-ems-text-primary uppercase tracking-wider">
            Site Security Alerts
          </h2>
          <span className="text-xs text-ems-text-muted">({alerts?.length || 0} alerts)</span>
        </div>

        {!alerts?.length ? (
          <p className="text-sm text-ems-text-muted text-center py-4">No security alerts for this site</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ems-border">
                  <th className="text-left px-3 py-2 text-xs font-semibold text-ems-text-muted uppercase">Severity</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-ems-text-muted uppercase">Category</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-ems-text-muted uppercase">Title</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-ems-text-muted uppercase">Status</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-ems-text-muted uppercase">Time</th>
                </tr>
              </thead>
              <tbody>
                {alerts.map((alert: any) => (
                  <tr key={alert.alert_id} className="border-b border-ems-border hover:bg-ems-bg-card-hover">
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                        alert.severity === 'critical' ? 'bg-ems-red/10 text-ems-red' :
                        alert.severity === 'high' ? 'bg-ems-amber/10 text-ems-amber' :
                        alert.severity === 'medium' ? 'bg-ems-amber/10 text-ems-amber' :
                        'bg-ems-sky/10 text-ems-sky'
                      }`}>
                        {alert.severity}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-ems-text-secondary">{alert.category}</td>
                    <td className="px-3 py-2 text-xs text-ems-text-primary">{alert.title}</td>
                    <td className="px-3 py-2 text-xs text-ems-text-secondary">{alert.status}</td>
                    <td className="px-3 py-2 text-xs text-ems-text-muted font-mono">
                      {new Date(alert.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Admin-only: Sessions and Login Activity */}
      {isAdmin && (
        <>
          <CollapsibleSection title="Site User Sessions" icon={Users}>
            <SessionTable isAdmin={isAdmin} />
          </CollapsibleSection>

          <CollapsibleSection title="Site Login Activity" icon={KeyRound}>
            <LoginActivityTable />
          </CollapsibleSection>
        </>
      )}
    </div>
  );
}
